import { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { BRAND, NAV_LINKS } from "@/data/siteData";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goApply = () => {
    setOpen(false);
    navigate("/book-a-stall");
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? "backdrop-blur-xl bg-white/85 border-b border-gray-100 shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-24 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo-link" className="flex items-center gap-2 shrink-0">
          <img src={BRAND.logo} alt="The Market Place logo" className="h-16 sm:h-20 w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.path}
              to={link.path}
              data-testid={`nav-link-${link.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) =>
                `text-sm tracking-wide transition-colors duration-300 hover:text-[#D81B60] ${
                  isActive ? "text-[#D81B60]" : "text-[#1A1A1A]"
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:block">
          <button
            data-testid="header-apply-stall-button"
            onClick={goApply}
            className="rounded-full bg-[#D81B60] text-white text-sm px-6 py-3 hover:bg-[#AD144B] hover:scale-[1.02] transition-all duration-300 shadow-sm"
          >
            Book a Stall
          </button>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden text-[#1A1A1A]"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden bg-white border-t border-gray-100 overflow-hidden"
          >
            <div className="flex flex-col px-6 py-6 gap-5">
              {NAV_LINKS.map((link) => (
                <NavLink
                  key={link.path}
                  to={link.path}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="text-base text-[#1A1A1A]"
                >
                  {link.label}
                </NavLink>
              ))}
              <button
                data-testid="mobile-apply-stall-button"
                onClick={goApply}
                className="rounded-full bg-[#D81B60] text-white text-sm px-6 py-3 mt-2"
              >
                Book a Stall
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
